import { Link } from 'react-router-dom';
import Hero from '@/components/Hero';
import TripleSection from '@/components/TripleSection';
import HowItWorks from '@/components/HowItWorks';
import PlatformsSection from '@/components/PlatformsSection';
import CTABanner from '@/components/CTABanner';
import s from '@/components/home.module.css';

export default function Home() {
  return (
    <>
      <Hero />

      <TripleSection
        id="what"
        label="What it is"
        title="Legal services, restructured for the AI era."
        lead="Hourly billing exists because the work was manual. AVOWAL automates the manual layer, which makes flat-fee, attorney-backed representation viable for matters that used to be either prohibitively expensive or impossible to staff."
        cards={[
          { num: '/ 01', title: 'Limited-scope representation', body: 'You hire an attorney for a specific task — a motion, a hearing, a contract review — not the whole matter. Defined, capped, court-approved where required. We make it the default, not the exception.' },
          { num: '/ 02', title: 'AI-managed workflow',         body: 'ELARIA handles intake, document assembly, deadline tracking, citation verification, and communications. Your attorney spends their time on judgment, not paperwork. The price reflects it.' },
          { num: '/ 03', title: 'White-label by design', body: "We don't employ lawyers, and we don't stand between you and yours. Clients work in their attorney's own portal — the firm's name, the firm's domain, an assistant the attorney names and configures. The attorney runs the practice; we run the operations layer." },
        ]}
      />

      <TripleSection
        id="agentic"
        label="Agentic by design"
        title="Agents, not chatbots. Yours, not ours."
        lead="The engine underneath is agentic: it holds a goal, plans the steps, executes them, and checks its own work. What your clients meet is your firm's assistant. Where it runs is your call."
        cards={[
          { num: '/ AGENTS', title: 'Agents, not chatbots', body: 'A chatbot answers one prompt and stops. An agent reads the file, drafts the motion, verifies its own citations, watches the deadlines, and comes back when a decision needs a lawyer.' },
          { num: '/ YOUR NAME', title: 'Your own named assistant', body: "Clients meet your firm's assistant — named by you, on your domain, in your brand. ELARIA is the engine underneath; the name on it is yours." },
          { num: '/ SOVEREIGN', title: 'Runs on your hardware', body: 'The same engine on machines the firm owns, inside your own walls. Can run fully offline — work product never leaves the building. Founding-firm program, by engagement.' },
        ]}
        foot={
          <Link to="/ai" className={s.moreLink}>
            Learn how agentic AI works &rarr;
          </Link>
        }
      />

      <HowItWorks />
      <PlatformsSection />

      <TripleSection
        id="elaria"
        label="The agent"
        title="ELARIA — the operations layer."
        lead="ELARIA is the AI agent that runs inside AVOWAL. Patent-pending architecture, built from the ground up for legal work: citation verification, hallucination detection, audit-trail evidence chains, and a bridge into court e-filing."
        cards={[
          { num: '/ DOES',     title: 'Operations',    body: 'Intake, document drafting, citation verification, deadline tracking, evidence organization, communications, filing prep, audit trails. The mechanical layer of practice.' },
          { num: '/ DOES NOT', title: 'Legal advice',  body: "ELARIA does not advise on strategy, predict outcomes, or substitute for an attorney. Every judgment call is escalated to the attorney. Every output is reviewed before filing." },
          { num: '/ NEVER',    title: 'Fabricates',    body: 'Every citation is verified against primary sources before it leaves the system. Every fact is traceable to its document. Hallucination is treated as a defect, not an acceptable failure mode.' },
        ]}
      />

      <TripleSection
        id="trust"
        label="Trust by construction"
        title="Built so you don't have to take our word for it."
        cards={[
          { num: '/ ATTORNEYS', title: 'Independent, licensed, accountable', body: 'Every attorney on the platform is independently licensed and personally accountable for the work they sign. AVOWAL never holds itself out as legal counsel.' },
          { num: '/ ETHICS',    title: 'Built around the UPL line',           body: 'Workflows are structured around the unauthorized-practice-of-law rules of each jurisdiction. ELARIA executes; attorneys advise. The line is clear and enforced in software.' },
          { num: '/ PROOF',     title: 'Cryptographically verifiable',        body: 'Every filing AVOWAL prepares carries a signed manifest of sources, citations, and the chain of edits. Verifiable by court, opposing counsel, or auditor.' },
        ]}
      />

      <CTABanner />
    </>
  );
}
